import React from "react";
import { Link } from "react-router-dom";
import { Check, ChevronLeft } from "lucide-react";

/**
 * Gemeinsamer Rahmen für 3-Schritt-Simple-Flows (MatchMixer / ClubChamp).
 * Steps/Content/Summary/Footer bleiben Slot-basiert — keine Fachlogik.
 *
 * @param {{
 *   flowStep: number,
 *   steps: Array<{ id: number, key: string }>,
 *   stepLabel: (step: { id: number, key: string }) => string,
 *   stepsAriaLabel: string,
 *   visitedSteps?: Set<number> | number[],
 *   onStepChange: (id: number) => void,
 *   backLabel: string,
 *   overviewHref?: string,
 *   onDone?: () => void,
 *   doneDisabled?: boolean,
 *   primaryBusy?: boolean,
 *   eventTitle: string,
 *   eventMeta?: string,
 *   summary?: React.ReactNode,
 *   footer?: React.ReactNode,
 *   children: React.ReactNode,
 *   dataApp?: string,
 * }} props
 */
export function SimpleFlowShell({
  flowStep,
  steps,
  stepLabel,
  stepsAriaLabel,
  visitedSteps,
  onStepChange,
  backLabel,
  overviewHref = "/",
  onDone = null,
  doneDisabled = false,
  primaryBusy = false,
  eventTitle,
  eventMeta = "",
  summary = null,
  footer = null,
  children,
  dataApp,
}) {
  const visited = React.useMemo(() => {
    if (visitedSteps instanceof Set) return visitedSteps;
    if (Array.isArray(visitedSteps)) return new Set(visitedSteps);
    return new Set([1]);
  }, [visitedSteps]);

  const goStep = (id) => {
    const n = Math.max(1, Math.min(steps.length, Number(id) || 1));
    onStepChange?.(n);
  };

  return (
    <div
      className="mm-simple"
      data-step={flowStep}
      data-app={dataApp || undefined}
    >
      <header className="mm-simple__top">
        {typeof onDone === "function" ? (
          <button
            type="button"
            className="mm-simple__backOverview"
            disabled={doneDisabled || primaryBusy}
            onClick={() => onDone()}
          >
            <ChevronLeft size={18} aria-hidden />
            <span>{backLabel}</span>
          </button>
        ) : (
          <Link to={overviewHref} className="mm-simple__backOverview">
            <ChevronLeft size={18} aria-hidden />
            <span>{backLabel}</span>
          </Link>
        )}
        <div className="mm-simple__eventHead">
          <h1 className="mm-simple__eventName">{eventTitle}</h1>
          {eventMeta ? (
            <p className="mm-simple__eventMeta">{eventMeta}</p>
          ) : null}
        </div>
        <nav className="mm-simple__steps" aria-label={stepsAriaLabel}>
          {steps.map((s, idx) => {
            const active = flowStep === s.id;
            const done = flowStep > s.id;
            const enabled = visited.has(s.id) || s.id <= flowStep || done;
            return (
              <React.Fragment key={s.id}>
                {idx > 0 ? (
                  <span className="mm-simple__stepLine" aria-hidden />
                ) : null}
                <button
                  type="button"
                  className={`mm-simple__step${active ? " is-active" : ""}${done ? " is-done" : ""}${enabled ? "" : " is-disabled"}`}
                  disabled={!enabled}
                  aria-current={active ? "step" : undefined}
                  onClick={() => enabled && goStep(s.id)}
                >
                  <span className="mm-simple__stepNum" aria-hidden>
                    {done ? <Check size={18} strokeWidth={2.5} /> : s.id}
                  </span>
                  <span className="mm-simple__stepLabel">{stepLabel(s)}</span>
                </button>
              </React.Fragment>
            );
          })}
        </nav>
      </header>

      <div className="mm-simple__layout">
        <div className="mm-simple__main">{children}</div>
        {summary}
      </div>
      {footer}
    </div>
  );
}

/**
 * Footer-Navigation für Simple-Flows (Zurück / Weiter / Speichern).
 */
export function SimpleFlowFooter({
  flowStep,
  maxStep = 3,
  onBack,
  onNext,
  nextLabel,
  backLabel,
  onDone,
  doneLabel,
  doneDisabled = false,
  primaryBusy = false,
  onPrimary,
  primaryLabel,
  primaryDisabled = false,
  primaryHint = "",
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
  secondaryHint = "",
  leading = null,
}) {
  const showHint = flowStep === maxStep && (primaryHint || secondaryHint);

  return (
    <footer className="mm-simple__cardFooter">
      {flowStep > 1 ? (
        <button type="button" className="mm-simple__btn" onClick={onBack}>
          {backLabel}
        </button>
      ) : typeof onDone === "function" ? (
        <button
          type="button"
          className="mm-simple__btn mm-simple__btn--ghost"
          disabled={doneDisabled || primaryBusy}
          onClick={() => onDone()}
        >
          {doneLabel}
        </button>
      ) : (
        leading || <span className="mm-simple__cardFooterSpacer" aria-hidden />
      )}
      {flowStep < maxStep ? (
        <div className="mm-simple__finishActions">
          {flowStep > 1 && typeof onDone === "function" ? (
            <button
              type="button"
              className="mm-simple__btn mm-simple__btn--ghost"
              disabled={doneDisabled || primaryBusy}
              onClick={() => onDone()}
            >
              {doneLabel}
            </button>
          ) : null}
          <button
            type="button"
            className="mm-simple__btn mm-simple__btn--primary"
            onClick={onNext}
          >
            {nextLabel}
          </button>
        </div>
      ) : (
        <div className="mm-simple__finishActions">
          {secondaryLabel && typeof onSecondary === "function" ? (
            <button
              type="button"
              className="mm-simple__btn mm-simple__btn--ghost"
              disabled={secondaryDisabled || primaryBusy}
              title={secondaryHint || undefined}
              onClick={() => onSecondary()}
            >
              {secondaryLabel}
            </button>
          ) : null}
          {typeof onDone === "function" ? (
            <button
              type="button"
              className="mm-simple__btn mm-simple__btn--ghost"
              disabled={doneDisabled || primaryBusy}
              onClick={() => onDone()}
            >
              {doneLabel}
            </button>
          ) : null}
          <button
            type="button"
            className="mm-simple__btn mm-simple__btn--primary"
            disabled={primaryDisabled || primaryBusy}
            aria-busy={primaryBusy || undefined}
            title={primaryHint || undefined}
            onClick={() => onPrimary?.()}
          >
            {primaryLabel}
          </button>
        </div>
      )}
      {showHint ? (
        <p className="mm-simple__finishHint" role="status">
          {primaryHint || secondaryHint}
        </p>
      ) : null}
    </footer>
  );
}
